import { Component, ErrorInfo } from 'react';
import { captureException } from '@sentry/react';

import { PageNotFound } from '@xemplo/page-not-found';

import { ErrorBoundaryProps, State } from './error-boundary.types';

const errorScreenDefaults = (error?: Error) => ({
  title: 'An unknown error has occurred',
  subtitle: error?.message,
});

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Logs the exception to Sentry so we can figure it out what's going on
    captureException(error, {
      extra: { errorInfo, name: this.props.name ?? 'Global' },
      tags: { from: 'error-boundary' },
    });
  }

  render() {
    if (this.state.hasError) {
      const { name, children, ...pageNotFoundProps } = this.props;
      const args = { ...errorScreenDefaults(this.state.error), ...pageNotFoundProps };
      return <PageNotFound {...args} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
